import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  styled,
  Typography,
  Tooltip,
} from '@mui/material';
import Heading from 'components/Heading';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InteractiveTable, {
  Column,
  useRowsEdit,
} from 'components/InteractiveTable';
import { useEffect, useState } from 'react';
import { ListLpFeesCategory } from 'further-types/lp/lp-fee';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useDisclosure } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import {
  FundingSourceLabels,
  LpFeeCalculationBasisLabels,
  LpFeeCommitmentStatusLabels,
} from 'further-ui/labels';
import { AppNumberInput, AppSimpleSelect } from 'components/FormElements';
import AlertDialog from 'components/AlertDialog';
import { GridAlignment, GridRowId } from '@mui/x-data-grid-pro';
import ButtonGroup from 'components/ButtonGroup';
import { FundingSource } from 'further-types/lp/investment';
import { round } from 'lodash';
import useFundingSourceOptions from 'hooks/form/useFundingSourceOptions';
import { ApprovalSummaryData } from '..';

const StyledAccordion = styled(Accordion, {
  shouldForwardProp: (prop) =>
    !['expanded', 'isApprovedOrDue'].includes(prop.toString()),
})<{ expanded: boolean; isApprovedOrDue: boolean }>(
  ({ theme, expanded, isApprovedOrDue }) => ({
    '&.MuiAccordion-root': {
      borderRadius: expanded ? '0px 0px 4px 4px' : '0px',
    },
    ...(isApprovedOrDue && {
      border: `1px solid ${theme?.palette.primary.main}`,
    }),
  }),
);

const StyledApprovedAccordionSummary = styled(AccordionSummary, {
  shouldForwardProp: (prop) => prop !== 'isApproved',
})<{ isApproved: boolean }>(({ isApproved }) => ({
  borderRadius: '0 0 4px 4px',
  ...(isApproved && {
    backgroundColor: '#F2FFF5',
  }),
  '& .MuiAccordionSummary-content': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  height: '80px',
}));

const StyledExpandText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(1),
}));

const StyledHeading = styled(Heading)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
}));

const StyledApprovedSubheading = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
}));

const StyledTable = styled(InteractiveTable)({
  borderRadius: '0 0 4px 4px',
  border: 'none',
  borderTop: '1px solid #E0E0E0',

  '& .MuiDataGrid-cell--editing': {
    '&[data-field="feeExTaxLcy"], &[data-field="salesTaxAmountLcy"], &[data-field="fundingSource"]':
      {
        '&:focus-within': {
          outline: 'none',
        },
      },
  },
});

const StyledAccordionDetails = styled(AccordionDetails)({ padding: 0 });

const StyledFullWidthSelect = styled(AppSimpleSelect)({
  width: '100%',
});

export type GroupedFees = Array<{
  initialTotal: number;
  accrualDate: CalendarDay;
  lpFees: ApprovalSummaryData['fullFeeData'];
}>;

export type GroupedFeesWithDefaultTransactions = {
  totalPages: number;
  feesTotalValue: number;
  defaultTransactions: ApprovalSummaryData['defaultTransactions'];
  groupedFees: GroupedFees;
};

const IndividualFeeAccordion: React.FC<{
  date: CalendarDay;
  feeList: GroupedFees[number]['lpFees'];
  feeType: ListLpFeesCategory;
  expandFirst: boolean;
  defaultTransactions: ApprovalSummaryData['defaultTransactions'];
  setApprovalSummaryData?: (data: ApprovalSummaryData) => void;
}> = ({
  date,
  feeList,
  feeType,
  expandFirst,
  defaultTransactions,
  setApprovalSummaryData,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const confirmDialog = useDisclosure();
  const { rowsEditModel, editManyRows } = useRowsEdit();
  const { options: fundingSourceOptions } = useFundingSourceOptions(
    selectedVehicleId ?? '',
    false,
  );
  const [expanded, setExpanded] = useState(expandFirst);
  const isApproved = feeType === ListLpFeesCategory.ApprovedFees;
  const [rows, setRows] = useState(() =>
    feeList.map((fee) => ({
      ...fee,
      id: fee._id,
      feeIncTaxLcy: fee.feeExTaxLcy + fee.salesTaxAmountLcy,
    })),
  );

  useEffect(() => {
    if (rows.length === 0) return;
    editManyRows(rows.map((row) => ({ id: row.id })));
    // Only run on mount
  }, []);

  const updateRow = ({
    id,
    feeExTaxLcy,
    salesTaxAmountLcy,
    fundingSource,
  }: {
    id: GridRowId;
    feeExTaxLcy: number;
    salesTaxAmountLcy: number;
    fundingSource: FundingSource;
  }) => {
    const feeIncTaxLcy = round(feeExTaxLcy + salesTaxAmountLcy, 2);
    setRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
              ...row,
              feeIncTaxLcy,
              feeExTaxLcy,
              salesTaxAmountLcy,
              fundingSource,
            }
          : row,
      ),
    );
  };

  const mutationOpts = useHandleTrpcMutation();
  const unapproveFeesMutation = api.lpFee.unapprove.useMutation(
    mutationOpts({
      successMessage: 'Fees unapproved successfully',
    }),
  );

  const handleUnapproveFees = async () => {
    await unapproveFeesMutation.mutate({
      vehicleId: selectedVehicleId ?? '',
      feeActionIds: Array.from(
        new Set(
          feeList.map((fee) => fee.feeActionId ?? '').filter((id) => id !== ''),
        ),
      ),
    });
  };

  const handleApproveAndProcess = async () => {
    setApprovalSummaryData?.({
      date: date.toLocalDateLabel(),
      fullFeeData: rows,
      defaultTransactions,
      feeType,
    });
  };

  const totalValue = rows.reduce((sum, row) => sum + row.feeIncTaxLcy, 0);

  const approvedDates = Array.from(
    new Set(rows.map((row) => row.feeActionCreatedAt.toLabel())),
  ).join(', ');
  const isFromEqualisation = feeList.some((fee) => fee.isFromEqualisation);
  const disableUnapprove = feeList.some((fee) => fee.disableUnapprove);

  const leftAlign: GridAlignment = 'left';
  const columns: Array<Column> = [
    {
      headerName: 'Limited partner',
      field: 'limitedPartner',
      flex: 1,
    },
    ...(isApproved
      ? [
          {
            headerName: 'Approval date',
            field: 'feeActionCreatedAt',
            flex: 1,
            renderCell: (params) => params.value.toLocalDateLabel(),
          },
        ]
      : []),
    {
      headerName: 'Accrual date',
      field: 'accrualDate',
      flex: 1,
      renderCell: (params) => params.value.toLocalDateLabel(),
    },
    {
      headerName: 'Template name',
      field: 'lpFeeTemplateName',
      flex: 1,
    },
    {
      headerName: 'Fee name',
      field: 'lpFeeTemplateFeeName',
      flex: 1,
    },
    {
      headerName: 'Inside/outside commitment',
      field: 'insideOutsideCommitment',
      flex: 1,
      renderCell: (params) => LpFeeCommitmentStatusLabels.get(params.value),
    },
    {
      headerName: 'Calculation basis',
      field: 'calculationBasis',
      flex: 1,
      renderCell: (params) => LpFeeCalculationBasisLabels.get(params.value),
    },
    {
      headerName: 'Fee',
      field: 'feeExTaxLcy',
      flex: 1,
      align: leftAlign,
      headerAlign: leftAlign,
      renderCell: (params) => numberToCurrencyString(params.value),
      editable: !isApproved,
      type: 'number',
      renderEditCell: (params) => (
        <AppNumberInput
          value={params.value}
          onChange={(newValue) => {
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: newValue,
            });

            updateRow({
              id: params.id,
              feeExTaxLcy: newValue,
              salesTaxAmountLcy: params.row.salesTaxAmountLcy,
              fundingSource: params.row.fundingSource,
            });
          }}
          currencyStartAdornment
          decimals={2}
          min={0}
          sx={{ marginTop: '2px' }}
        />
      ),
    },
    {
      headerName: 'Sales tax',
      field: 'salesTaxAmountLcy',
      flex: 1,
      align: leftAlign,
      headerAlign: leftAlign,
      renderCell: (params) => numberToCurrencyString(params.value),
      editable: !isApproved,
      type: 'number',
      renderEditCell: (params) => (
        <AppNumberInput
          value={params.value}
          onChange={(newValue) => {
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: newValue,
            });

            updateRow({
              id: params.id,
              feeExTaxLcy: params.row.feeExTaxLcy,
              salesTaxAmountLcy: newValue,
              fundingSource: params.row.fundingSource,
            });
          }}
          currencyStartAdornment
          decimals={2}
          min={0}
          sx={{ marginTop: '2px' }}
        />
      ),
    },
    {
      headerName: 'Total charge',
      field: 'feeIncTaxLcy',
      flex: 1,
      renderCell: (params) => numberToCurrencyString(params.value),
    },
    ...(isApproved
      ? [
          {
            headerName: 'Capital call/General capital payment',
            field: 'lpFeePaymentDate',
            flex: 1,
            renderCell: (params) => params.value,
          },
        ]
      : [
          {
            headerName: 'General capital',
            field: 'generalCapital',
            flex: 1,
            renderCell: (params) => numberToCurrencyString(params.value ?? 0),
          },
          {
            headerName: 'Pay fees by',
            field: 'fundingSource',
            flex: 1,
            renderCell: (params) =>
              FundingSourceLabels[params.value] ?? params.value,
            editable: !isApproved,
            align: leftAlign,
            headerAlign: leftAlign,
            renderEditCell: (params) => (
              <StyledFullWidthSelect
                value={params.value}
                options={fundingSourceOptions}
                disabled={params.row.generalCapital <= 0}
                onChange={(e) => {
                  params.api.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: e.target.value,
                  });
                  updateRow({
                    id: params.id,
                    feeExTaxLcy: params.row.feeExTaxLcy,
                    salesTaxAmountLcy: params.row.salesTaxAmountLcy,
                    fundingSource: e.target.value as FundingSource,
                  });
                }}
              />
            ),
          },
        ]),
  ];

  return (
    <>
      <StyledAccordion
        key={`${date.toString()}-${feeType}`}
        defaultExpanded={expanded}
        onChange={() => setExpanded(!expanded)}
        isApprovedOrDue={
          feeType === ListLpFeesCategory.ApprovedFees ||
          feeType === ListLpFeesCategory.DueUnapprovedFees
        }
        expanded={expanded}
      >
        <StyledApprovedAccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`fees-${date}`}
          id={`fees-header-${date}`}
          isApproved={feeType === ListLpFeesCategory.ApprovedFees}
        >
          <StyledHeading
            variant="h6"
            noMargin
            subtitle={
              <>
                {feeType === ListLpFeesCategory.ApprovedFees && (
                  <StyledApprovedSubheading>
                    Reviewed and approved on: {approvedDates}
                  </StyledApprovedSubheading>
                )}
              </>
            }
          >
            Management fees{' '}
            {isFromEqualisation ? 'various dates' : date.toLocalDateLabel()} -{' '}
            {numberToCurrencyString(totalValue)}
            {isFromEqualisation && ' (Approved via equalisation)'}
          </StyledHeading>
          <StyledExpandText>
            {expanded ? 'Hide fees' : 'Show fees'}
          </StyledExpandText>
        </StyledApprovedAccordionSummary>

        <StyledAccordionDetails>
          <StyledTable
            id={`fees-table-${date}`}
            rows={rows}
            columns={columns}
            rowsEditModel={rowsEditModel}
          />
        </StyledAccordionDetails>
      </StyledAccordion>
      {expanded && (
        <ButtonGroup>
          {disableUnapprove ? (
            <Tooltip
              title={
                isFromEqualisation
                  ? 'It is not possible to unapprove fees approved via equalisation'
                  : 'It is not possible to unapprove fees once capital has been called'
              }
            >
              <span>
                <Button
                  variant={'outlined'}
                  color={'secondary'}
                  disabled={true}
                >
                  Unapprove
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              variant={isApproved ? 'outlined' : 'contained'}
              color={isApproved ? 'secondary' : 'primary'}
              onClick={
                isApproved ? confirmDialog.onOpen : handleApproveAndProcess
              }
              disabled={unapproveFeesMutation.isPending}
            >
              {isApproved ? 'Unapprove' : 'Approve and process'}
            </Button>
          )}
        </ButtonGroup>
      )}
      {isApproved && (
        <AlertDialog
          open={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          title={'Confirm Unapproval'}
          content={
            'Are you sure you want to unapprove these fees? This will remove the transactions from the General Ledger and move the fees back to an unapproved state.'
          }
          btnLabels={{
            confirm: 'Confirm',
            cancel: 'Cancel',
          }}
          onConfirm={handleUnapproveFees}
          confirmBtnProps={{
            disabled: unapproveFeesMutation.isPending,
            loading: unapproveFeesMutation.isPending,
          }}
        />
      )}
    </>
  );
};

export default IndividualFeeAccordion;
